export const LIFE_CATEGORIES = [
  'mind',
  'health',
  'relationships',
  'career',
  'finance',
  'creative',
] as const
export type LifeCategory = (typeof LIFE_CATEGORIES)[number]

const ALIASES: Record<string, LifeCategory> = {
  fitness: 'health',
  workout: 'health',
  gym: 'health',
  run: 'health',
  running: 'health',
  sleep: 'health',
  water: 'health',
  meditation: 'mind',
  meditate: 'mind',
  journal: 'mind',
  focus: 'mind',
  read: 'mind',
  reading: 'mind',
  spiritual: 'mind',
  family: 'relationships',
  friends: 'relationships',
  social: 'relationships',
  love: 'relationships',
  work: 'career',
  job: 'career',
  study: 'career',
  learn: 'career',
  money: 'finance',
  budget: 'finance',
  save: 'finance',
  saving: 'finance',
  art: 'creative',
  write: 'creative',
  writing: 'creative',
  music: 'creative',
  draw: 'creative',
}

// First #tag that names a category (or a common alias for one).
export function categoryFromTags(tags: string[]): LifeCategory | undefined {
  for (const raw of tags) {
    const tag = raw.toLowerCase()
    if ((LIFE_CATEGORIES as readonly string[]).includes(tag)) return tag as LifeCategory
    if (ALIASES[tag]) return ALIASES[tag]
  }
  return undefined
}

// Extra goal-sentence words; kept apart from ALIASES so #tag matching is unchanged.
const GOAL_KEYWORDS: Record<string, LifeCategory> = {
  marathon: 'health',
  weight: 'health',
  pounds: 'health',
  kg: 'health',
  diet: 'health',
  yoga: 'health',
  healthy: 'health',
  fit: 'health',
  swim: 'health',
  surf: 'health',
  surfing: 'health',
  hike: 'health',
  hiking: 'health',
  climb: 'health',
  climbing: 'health',
  cycle: 'health',
  cycling: 'health',
  bike: 'health',
  ski: 'health',
  skiing: 'health',
  tennis: 'health',
  football: 'health',
  soccer: 'health',
  basketball: 'health',
  boxing: 'health',
  triathlon: 'health',
  '5k': 'health',
  '10k': 'health',
  promoted: 'career',
  promotion: 'career',
  salary: 'career',
  startup: 'career',
  business: 'career',
  saas: 'career',
  launch: 'career',
  engineer: 'career',
  interview: 'career',
  degree: 'career',
  certification: 'career',
  books: 'mind',
  language: 'mind',
  spanish: 'mind',
  french: 'mind',
  japanese: 'mind',
  mindful: 'mind',
  stress: 'mind',
  anxiety: 'mind',
  debt: 'finance',
  loans: 'finance',
  invest: 'finance',
  investing: 'finance',
  retire: 'finance',
  deposit: 'finance',
  income: 'finance',
  partner: 'relationships',
  wife: 'relationships',
  husband: 'relationships',
  kids: 'relationships',
  dating: 'relationships',
  wedding: 'relationships',
  parents: 'relationships',
  novel: 'creative',
  paint: 'creative',
  painting: 'creative',
  guitar: 'creative',
  piano: 'creative',
  album: 'creative',
  film: 'creative',
  podcast: 'creative',
  youtube: 'creative',
  photography: 'creative',
}

// Generic verbs lose ties, so "learn Spanish" reads as Mind rather than Career, and never decide alone.
const WEAK_WORDS = new Set(['learn', 'start', 'build', 'get', 'become'])

// Best-guess category for a free-text goal: the category with the most keyword hits.
export function categoryFromText(text: string): LifeCategory | undefined {
  const votes = new Map<LifeCategory, number>()
  for (const word of text.toLowerCase().match(/[\p{L}\p{N}$]+/gu) ?? []) {
    const cat: LifeCategory | undefined = word.includes('$')
      ? 'finance'
      : (GOAL_KEYWORDS[word] ?? categoryFromTags([word, word.replace(/s$/, '')]))
    if (cat) votes.set(cat, (votes.get(cat) ?? 0) + (WEAK_WORDS.has(word) ? 0.5 : 1))
  }
  let best: LifeCategory | undefined
  let max = 0
  for (const [cat, n] of votes) {
    if (n > max) {
      best = cat
      max = n
    }
  }
  // Only a generic verb matched ("learn to surf"): stay on Auto and let the planner decide.
  return max >= 1 ? best : undefined
}
